import React from "react";
import useSearch from "redux/hooks/useSearch";
import useRouter from "hooks/useRouter";
import MenuItemCarousel from "components/Shared/MenuItemCarousel";
import DealsCarousel from "components/Shared/DealsCarousel";
import {
  BotanicIcon,
  Button,
  Image,
  CarouselV2,
} from "@leafly-com/frontend-components";
import { localizedDistance } from "utils/localizedDistance";
import { trackEvent, Category, Action } from "utils/trackEvent";
import { CATEGORY_NAMES } from "common/constants";
import { useSplit } from "@leafly-com/frontend-components";
import { getCountryCode, getUserLocation } from "redux/selectors/location";
import { useSelector } from "react-redux";

const MenuItemResults: React.FC = () => {
  const countryCode = useSelector(getCountryCode);
  const coordinates = useSelector(getUserLocation);
  const { menuItemCategories, menuItemDispensaries, searchTerm } = useSearch();

  const {
    parsedQuery: { q },
    stringify,
  } = useRouter();

  const { selectors } = useSplit();

  const shopIngressButtonTreatment = selectors?.selectTreatmentType(
    "globalSearch_shopIngressButton_frontend"
  );

  const displayCategories = Object.keys(CATEGORY_NAMES).filter((category) =>
    menuItemCategories?.includes(category)
  );

  return (
    <>
      <h3 className="heading--l">Products</h3>
      <span className="font-normal">matching "{q}"</span>

      {menuItemDispensaries.map(
        ({
          city,
          deliveryEnabled,
          distanceMi,
          name,
          pickupEnabled,
          slug,
          state,
        }) => {
          const distance = localizedDistance(distanceMi, countryCode);
          const showDistance = !deliveryEnabled || pickupEnabled;
          const subHeader =
            distance && showDistance
              ? `${distance} away - ${city}, ${state}`
              : `${city}, ${state}`;

          const handleCarouselActionClick = (name: string, origin: string) => {
            const label =
              origin === "menu-item-carousel-header"
                ? `dispensary menu link - ${name}`
                : `show all matching products button - ${name}`;
            trackEvent(
              Category.searchResults,
              Action.searchResultClicked,
              label
            );
          };

          const handleMenuItemCardClick = (name: string) => {
            trackEvent(
              Category.searchResults,
              Action.searchResultClicked,
              `menu item card - ${name}`
            );
          };

          const dispensaryLink = `/dispensary-info/${slug}/menu?${stringify({
            q: searchTerm,
          })}#products`;

          return (
            <MenuItemCarousel
              key={`${name}-${q}`}
              header={name}
              headerLink={dispensaryLink}
              subheader={subHeader}
              cardsPerSlide={3}
              actionText={"Show %tc matching products"}
              actionHref={dispensaryLink}
              actionOnClick={(_, origin) =>
                handleCarouselActionClick(name, origin)
              }
              cardOnClick={() => handleMenuItemCardClick(name)}
              sort={{ 0: { serp_shop: "asc" } }}
              slug={slug}
              query={searchTerm}
              coordinates={coordinates}
              countryCode={countryCode}
              className="mt-xl"
            />
          );
        }
      )}

      <Button
        className="flex items-center justify-center mt-xxl mb-section"
        href={`/search/shop?${stringify({ q: searchTerm })}`}
        onClick={() =>
          trackEvent(
            Category.searchResults,
            Action.searchResultClicked,
            "shop all nearby menus button"
          )
        }
        variant={shopIngressButtonTreatment === "on" ? "primary" : "secondary"}
      >
        <BotanicIcon className="mr-sm" name="bag" /> shop all nearby menus
      </Button>

      {!!displayCategories.length && (
        <>
          <CarouselV2
            title={
              <span className="heading--m font-bold">
                Shop products by category
              </span>
            }
            touchCardWidth={120}
            className="mt-xl"
          >
            {displayCategories.map((category) => (
              <a
                key={category}
                href={`/search/shop?product_category=${category}`}
                onClick={() =>
                  trackEvent(
                    Category.searchResults,
                    Action.searchResultClicked,
                    `product type quick access - ${category}`
                  )
                }
                className="text-center"
              >
                <Image
                  src={`https://leafly-public.imgix.net/web-dispensary/category-images/${category.toLowerCase()}_300.png`}
                  alt={category}
                  sizes={[100]}
                  className="rounded-full bg-white elevation-low"
                />
                <div className="text-green text-sm font-bold mt-lg">
                  {CATEGORY_NAMES[category]}
                </div>
              </a>
            ))}
          </CarouselV2>
        </>
      )}

      <DealsCarousel className="mt-section" />
    </>
  );
};

export default MenuItemResults;
